import type { UserLocation } from '@/types/movement'

export interface MovementInfo {
    icon: string
    rotation: number
    label: string | null
}

const MOVING_SPEED_THRESHOLD_MPS = 2.2

export function headingToDirection(heading: number): string {
    const normalized = ((heading % 360) + 360) % 360
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
    const index = Math.round(normalized / 45) % 8
    return directions[index]
}

export function getMovementIcon(movementType: string, isMoving: boolean, speed = 0): string {
    const effectivelyMoving = isMoving || speed > MOVING_SPEED_THRESHOLD_MPS
    if (!effectivelyMoving) {
        return 'circle'
    }
    switch (movementType) {
        case 'walking':
        case 'on_foot':
            return 'walk'
        case 'running':
            return 'run'
        case 'on_bicycle':
            return 'bike'
        case 'in_vehicle':
            return 'truck'
        default:
            return 'navigation'
    }
}

export function getMovementLabel(
    movementType: string,
    isMoving: boolean,
    heading: number,
    speed = 0
): string | null {
    const effectivelyMoving = isMoving || speed > MOVING_SPEED_THRESHOLD_MPS
    if (!effectivelyMoving) return 'stationary'
    const direction = headingToDirection(heading)
    switch (movementType) {
        case 'walking':
        case 'on_foot':
            return `walking ${direction}`
        case 'running':
            return `running ${direction}`
        case 'on_bicycle':
            return `cycling ${direction}`
        case 'in_vehicle':
            return `driving ${direction}`
        default:
            return `moving ${direction}`
    }
}

export function getMovementInfo(
    isMoving: boolean,
    movementType: string,
    heading: number,
    speed = 0
): MovementInfo {
    const effectivelyMoving = isMoving || speed > MOVING_SPEED_THRESHOLD_MPS
    const icon = getMovementIcon(movementType, isMoving, speed)
    const label = getMovementLabel(movementType, isMoving, heading, speed)

    if (!effectivelyMoving) {
        return { icon, rotation: 0, label }
    }

    const rotation = icon === 'navigation' ? heading : heading - 90
    return { icon, rotation, label }
}

export function getRotationStyle(rotation: number, allowFlip = true) {
    if (rotation === 0) return undefined

    // Normalize rotation to -180 to 180 range for easier reasoning
    let normalizedRotation = rotation % 360
    if (normalizedRotation > 180) normalizedRotation -= 360
    if (normalizedRotation < -180) normalizedRotation += 360

    // Flip when icon would be upside down (pointing more left than right)
    // This is when rotation is outside -90 to 90 range
    const needsFlip = allowFlip && (normalizedRotation > 90 || normalizedRotation < -90)

    if (needsFlip) {
        // When flipping horizontally (scaleY: -1), we negate the rotation
        // to maintain the correct direction
        return {
            transform: [{ scaleY: -1 as const }, { rotate: `${-normalizedRotation}deg` }],
        }
    }

    return {
        transform: [{ rotate: `${normalizedRotation}deg` }],
    }
}

export function hasValidLocation(user: UserLocation): user is UserLocation & {
    mostRecentLocation: NonNullable<UserLocation['mostRecentLocation']>
} {
    return user.visible && user.mostRecentLocation != null
}

const TRAVEL_ROUTE_ARROW_RE = / (?:→|->) /

// Travel descriptions are generated server-side and stored on the first line
// of `notes`. Older entries use ASCII " -> "; new ones use the U+2192 arrow.
export function isTravelRouteNotes(notes: string | undefined | null): boolean {
    return !!notes && TRAVEL_ROUTE_ARROW_RE.test(notes)
}
